import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchMe, fetchPlans, fetchPlanQuote, checkout, validatePromo, INCLUDED_DEVICES, type Plan } from '../lib/api'
import { AppHeader } from '../components/AppHeader'
import { PageFrame } from '../components/PageFrame'
import { DeviceQuote } from '../components/DeviceQuote'
import { StickyTotal } from '../components/Form'
import { haptic, openExternal, tg } from '../lib/tg'

function notify(msg: string) {
  const wa = tg()
  if (wa?.showPopup) wa.showPopup({ message: msg })
  else alert(msg)
}

const MAX_DEVICES = 10
const INCLUDES = ['3 устройства', 'безлимит', 'Россия + Европа', 'турбо']

type Method = 'sbp' | 'crypto'

export function Plans() {
  const qc = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const { data: plans } = useQuery({ queryKey: ['plans'], queryFn: fetchPlans })

  const sorted = (Array.isArray(plans) ? plans : []).slice().sort((a, b) => a.duration_days - b.duration_days)
  const monthly = sorted.find(p => p.duration_days <= 31)
  const [planId, setPlanId] = useState<number | null>(null)
  const [devices, setDevices] = useState(INCLUDED_DEVICES)
  const [method, setMethod] = useState<Method>('sbp')
  const [busy, setBusy] = useState(false)
  const [promo, setPromo] = useState('')
  const [promoPct, setPromoPct] = useState(0)   // applied discount % (0 = none/unchecked)

  const plan: Plan | undefined =
    sorted.find(p => p.id === planId) ?? sorted.find(p => p.is_popular) ?? sorted[0]

  // Backend is the single source of price truth.
  const { data: quote } = useQuery({
    queryKey: ['planQuote', plan?.id, devices],
    queryFn: () => fetchPlanQuote(plan!.id, devices),
    enabled: !!plan,
  })

  const fullTotal = quote?.total ?? plan?.price_rub ?? 0
  const total = promoPct ? Math.round(fullTotal * (100 - promoPct) / 100) : fullTotal
  const totalMonths = plan ? Math.max(1, Math.round(plan.duration_days / 30)) : 1
  const perMonthTotal = Math.round(total / totalMonths)

  const checkPromo = async () => {
    const code = promo.trim()
    if (!code) { setPromoPct(0); return }
    try {
      const r = await validatePromo(code)
      setPromoPct(r.valid ? (r.discount_percent || 0) : 0)
      if (!r.valid) { haptic('light'); notify('Промокод не найден или истёк') }
    } catch { setPromoPct(0) }
  }

  const active = !!me?.is_active
  const used = me?.devices_used ?? 0
  const limit = me?.device_limit ?? INCLUDED_DEVICES

  const metrics = (p: Plan) => {
    const months = Math.max(1, Math.round(p.duration_days / 30))
    return {
      perMonth: Math.round(p.price_rub / months),
      save: monthly ? Math.max(0, Math.round(monthly.price_rub * months - p.price_rub)) : 0,
    }
  }

  const pay = async () => {
    if (!plan) return
    haptic('medium'); setBusy(true)
    try {
      const res = await checkout({ plan_id: plan.id, devices, method, promo: promo.trim() || undefined })
      if (res.kind === 'url' && res.url) openExternal(res.url)
      else if (res.kind === 'done') { qc.invalidateQueries({ queryKey: ['me'] }); notify('Подписка активна!') }
      else notify('Оплата временно недоступна. Попробуй позже.')
    } catch { notify('Оплата временно недоступна. Попробуй позже.') }
    finally { setBusy(false) }
  }

  return (
    <>
      <AppHeader />
      <PageFrame title="тарифы" subtitle={active ? 'продли или смени план' : 'выбери подписку'}>
        <div className="px-5 space-y-4">

          {/* renewal context — one line (header already shows days; no status block) */}
          {active && (
            <div className="num text-[11px]" style={{ color: 'var(--color-muted)' }}>
              <b style={{ color: 'var(--color-ink)' }}>{used} / {limit} устройств</b> · продление добавит дни к текущим
            </div>
          )}

          {/* what's included — chips */}
          <div>
            <div className="label mb-2.5">во всех тарифах</div>
            <div className="flex flex-wrap gap-1.5">
              {INCLUDES.map(t => (
                <span key={t} className="num text-[10.5px] px-2 py-1 rounded-[5px] card-soft"
                  style={{ color: 'var(--color-muted)' }}>{t}</span>
              ))}
            </div>
          </div>

          {/* plans — 3 columns (pricing table), хит accented */}
          <div>
            <div className="label mb-3.5">период</div>
            <div className="grid grid-cols-3 gap-2">
              {sorted.map(p => {
                const sel = plan?.id === p.id
                const hit = !!p.is_popular
                const { perMonth, save } = metrics(p)
                const months = Math.max(1, Math.round(p.duration_days / 30))
                return (
                  <button key={p.id} onClick={() => { haptic('light'); setPlanId(p.id) }}
                    className="tap relative rounded-[9px] px-1.5 py-3 text-center"
                    style={{
                      border: sel ? '2px solid var(--color-accent)' : `1px solid ${hit ? 'var(--color-line-strong)' : 'var(--color-line)'}`,
                      background: sel ? 'var(--color-accent-soft)' : hit ? 'var(--color-surface-2)' : 'var(--color-surface)',
                    }}>
                    {hit && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 display text-[8.5px] tracking-[0.08em] px-1.5 py-0.5 rounded-[4px]"
                        style={{ background: 'var(--color-ink)', color: 'var(--color-ink-inv)' }}>хит</span>
                    )}
                    <div className="display text-[13px]" style={{ color: (sel || hit) ? 'var(--color-ink)' : 'var(--color-muted)' }}>{months} мес</div>
                    <div className="num text-[22px] font-bold mt-2 leading-none">{perMonth}</div>
                    <div className="num text-[9px] mt-1" style={{ color: 'var(--color-muted)' }}>₽ / мес</div>
                    <div className="num text-[11px] mt-2.5 pt-2.5 border-t" style={{ borderColor: 'var(--color-line)' }}>{p.price_rub} ₽</div>
                    <div className="num text-[9.5px] font-bold mt-1"
                      style={{ color: 'var(--color-accent)', visibility: save > 0 ? 'visible' : 'hidden' }}>−{save} ₽</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* devices */}
          <div>
            <div className="label mb-2.5">устройства</div>
            <DeviceQuote
              mode="tariff"
              devices={devices}
              included={INCLUDED_DEVICES}
              max={MAX_DEVICES}
              onChange={(n) => { haptic('light'); setDevices(n) }}
              dayRate={quote?.day_rate ?? 3.33}
              extraDevices={quote?.extra_devices ?? 0}
              extraCost={quote?.extra_cost ?? 0}
            />
          </div>

          {/* payment */}
          <div>
            <div className="label mb-2.5">оплата</div>
            <div className="grid grid-cols-2 gap-2">
              {([['sbp', 'СБП', 'быстрый платёж'], ['crypto', 'Крипта', 'USDT']] as const).map(([m, name, hint]) => {
                const on = method === m
                return (
                  <button key={m} onClick={() => { haptic('light'); setMethod(m) }}
                    className="tap rounded-[8px] px-3 py-2.5 text-left"
                    style={{
                      background: on ? 'var(--color-accent-soft)' : 'var(--color-surface-2)',
                      border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-line)'}`,
                    }}>
                    <div className="display text-[14px]" style={{ color: on ? 'var(--color-accent)' : 'var(--color-ink)' }}>{name}</div>
                    <div className="num text-[10px] mt-0.5" style={{ color: 'var(--color-muted)' }}>{hint}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* promo code */}
          <div>
            <div className="label mb-2.5">промокод</div>
            <div className="flex gap-2">
              <input
                value={promo}
                onChange={(e) => { setPromo(e.target.value); setPromoPct(0) }}
                onBlur={checkPromo}
                placeholder="если есть"
                autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                className="num flex-1 rounded-[8px] px-3 py-2.5 text-[14px] outline-none"
                style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-line)', color: 'var(--color-ink)' }}
              />
              <button onClick={checkPromo} className="tap rounded-[8px] px-4 text-[13px] display"
                style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-line)', color: 'var(--color-ink)' }}>
                применить
              </button>
            </div>
            {promoPct > 0 && (
              <div className="num text-[11px] mt-1.5" style={{ color: 'var(--color-accent)' }}>
                ✓ скидка {promoPct}% · −{Math.round(fullTotal * promoPct / 100)} ₽
              </div>
            )}
          </div>

        </div>
      </PageFrame>

      <StickyTotal
        label={plan ? `${plan.name} · ${devices} устр${active ? ' · продление' : ''}` : 'выбери план'}
        price={`${total} ₽`}
        hint={plan && totalMonths > 1 ? `${perMonthTotal} ₽/мес` : undefined}
        onPay={pay}
        cta={busy ? 'создаём…' : `оплатить · ${method === 'crypto' ? 'Крипта' : 'СБП'}`}
        disabled={busy || !plan}
      />
    </>
  )
}
